import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { typography, spacing, borderRadius, animation, sizing } from '../../theme';
import { lockScroll, unlockScroll } from '../../utils/scroll-lock';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '900px',
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      lockScroll();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (isOpen) unlockScroll();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <Overlay onClick={onClose}>
      <Content onClick={(e) => e.stopPropagation()} maxWidth={maxWidth}>
        {title && (
          <Header>
            <Title>{title}</Title>
            <CloseButton onClick={onClose}>✕</CloseButton>
          </Header>
        )}
        {!title && <CloseButtonOnly onClick={onClose}>✕</CloseButtonOnly>}
        <Body hasHeader={!!title}>{children}</Body>
      </Content>
    </Overlay>,
    document.body
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.xl}px;
  backdrop-filter: blur(4px);
  animation: fadeIn ${animation.duration.fast} ${animation.easing.easeOut};

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Content = styled.div<{ maxWidth: string }>`
  background: var(--bg-elevated);
  border-radius: ${borderRadius.lg}px;
  max-width: ${props => props.maxWidth};
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  border: 1px solid var(--border-default);
  box-shadow: var(--shadow-lg);
  animation: slideUp ${animation.duration.normal} ${animation.easing.easeOut};

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.lg}px ${spacing.xl}px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--accent-subtle);
`;

const Title = styled.h2`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: var(--fg-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  width: ${sizing.button.md}px;
  height: ${sizing.button.md}px;
  border-radius: 50%;
  border: none;
  background: var(--bg-hover);
  color: var(--fg-primary);
  font-size: ${sizing.icon.sm}px;
  cursor: pointer;
  transition: all ${animation.duration.fast};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--danger-bg);
    border: 1px solid var(--danger-border);
  }
`;

const CloseButtonOnly = styled(CloseButton)`
  position: absolute;
  top: ${spacing.lg}px;
  right: ${spacing.lg}px;
  z-index: 1;
`;

const Body = styled.div<{ hasHeader: boolean }>`
  padding: ${spacing.xl}px;
  overflow-y: auto;
  max-height: ${props => props.hasHeader ? 'calc(90vh - 80px)' : 'calc(90vh - 32px)'};

  &::-webkit-scrollbar {
    width: ${sizing.scrollbar}px;
  }

  &::-webkit-scrollbar-track {
    background: var(--bg-hover);
    border-radius: ${borderRadius.xs}px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-strong);
    border-radius: ${borderRadius.xs}px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--fg-muted);
  }
`;
